import {
  ChatIcon,
  FlexBox,
  JoonColorExpanded,
  SPACING,
  Typography,
  VideoIcon,
} from "@joonapp/web-shared"
import { JoonUIColor } from "@joonapp/web-shared"
import { useId, useMemo } from "react"

import useSubscriptionTypes from "./hooks/useSubscriptionTypes"
import Tooltip from "../../components/tooltip/Tooltip"
import {
  useCoachingGroupsQuery,
  useCoachDirectReportsQuery,
  useCoachSubscriptions,
} from "../../networking/coachingGroups/queries"

const SummarySection = () => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const { data: coachDirectReports } = useCoachDirectReportsQuery()
  const availableSlotsTooltipId = useId()
  const { data: coachingSubscriptions } = useCoachSubscriptions()
  const { numVideoSubs, numChatSubs } = useSubscriptionTypes(
    coachingSubscriptions || []
  )
  const coachProfiles = coachDirectReports?.map(
    (report) => report.coach_profile
  )

  const numCoaches = coachProfiles?.length

  const { numAvailableSlotsByCoach, totalSlotsAvailable } = useMemo(() => {
    if (!coachingGroups || !coachProfiles)
      return { numAvailableSlotsByCoach: [], totalSlotsAvailable: 0 }

    const numAvailableSlotsByCoach = coachProfiles?.map((profile) => {
      const numCurrentClients = coachingGroups.filter((group) =>
        group.profiles.some((p) => p.id === profile.id)
      ).length
      return {
        name: profile.user.name,
        availableSlots: profile.max_available_slots - numCurrentClients,
        shownSlots: profile.num_open_slots,
      }
    })

    const totalSlotsAvailable = numAvailableSlotsByCoach.reduce(
      (acc, curr) => acc + curr.availableSlots,
      0
    )

    const sortedSlots = numAvailableSlotsByCoach.sort(
      (a, b) => b.availableSlots - a.availableSlots
    )

    return { numAvailableSlotsByCoach: sortedSlots, totalSlotsAvailable }
  }, [coachProfiles, coachingGroups])

  return (
    <FlexBox
      direction="column"
      gap={SPACING.space2}
      style={{
        userSelect: "none",
        width: "fit-content",
        whiteSpace: "nowrap",
      }}
    >
      <FlexBox direction="row" gap={SPACING.space4} wrap={false} align="center">
        <FlexBox
          direction="column"
          align="center"
          style={{ width: "fit-content" }}
        >
          <Typography variant="h3" textAlign="center">
            {numCoaches}
          </Typography>
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            Coaches
          </Typography>
        </FlexBox>
        <div
          style={{
            width: "1px",
            height: "50px",
            background: JoonUIColor.border.default,
          }}
        />

        <FlexBox
          direction="row"
          wrap={false}
          align="center"
          gap={SPACING.space4}
          style={{ width: "fit-content" }}
        >
          <FlexBox direction="column" align="center">
            <Typography variant="h3" textAlign="center">
              {coachingGroups?.length || 0}
            </Typography>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              Clients
            </Typography>
          </FlexBox>
          <FlexBox direction="column" style={{ width: "fit-content" }}>
            <FlexBox
              direction="row"
              gap={SPACING.space2}
              wrap={false}
              align="center"
            >
              <VideoIcon size={13} color={JoonColorExpanded.purple600} />
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                {numVideoSubs}
              </Typography>
            </FlexBox>
            <FlexBox
              direction="row"
              gap={SPACING.space2}
              wrap={false}
              align="center"
            >
              <ChatIcon size={12} color={JoonColorExpanded.purple600} />
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                {numChatSubs}
              </Typography>
            </FlexBox>
          </FlexBox>
        </FlexBox>

        <div
          style={{
            width: "1px",
            height: "50px",
            background: JoonUIColor.border.default,
          }}
        />

        <FlexBox
          direction="column"
          align="center"
          data-tooltip-id={availableSlotsTooltipId}
          style={{ width: "fit-content" }}
        >
          <Typography variant="h3" textAlign="center">
            {totalSlotsAvailable}
          </Typography>
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            Slots available
          </Typography>
          <Tooltip id={availableSlotsTooltipId}>
            {numAvailableSlotsByCoach.map((slot) => (
              <FlexBox direction="row" gap={SPACING.space2}>
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.inverted}
                  style={{ width: "50px" }}
                >
                  {slot.name}
                </Typography>
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.inverted}
                >
                  {isNaN(slot.availableSlots) ? "-" : slot.availableSlots}
                </Typography>
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.secondary}
                >
                  ({slot.shownSlots})
                </Typography>
              </FlexBox>
            ))}
          </Tooltip>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

export default SummarySection
